import { Type } from '@angular/core';
import { PortfolioBaseType, PortfolioComponentId } from '@types';
import { DocumentNode } from '@apollo/client/core';
import assetAllocationQuery from '@graphql/portfolio/asset-allocation.graphql';
import sectorAllocationQuery from '@graphql/portfolio/sector-allocation.graphql';
import topHoldingsQuery from '@graphql/portfolio/top-holdings.graphql';
import { Mapper } from '@products/utils/mappers/type.mapper';
import { AssetAllocationMapper } from '@products/utils/mappers/portfolio/asset-allocation.mapper';
import {
  SectorAllocationMapper,
  SectorAllocationsTableMapper,
} from '@products/utils/mappers/portfolio/sector-allocation.mapper';
import {
  TopHoldingsMapper,
  TopHoldingsTableMapper,
} from '@products/utils/mappers/portfolio/topholdings.mapper';
import { PortfolioChartsComponent } from './portfolio-charts/portfolio-charts.component';
import { SectorBreakdownComponent } from './sector-breakdown/sector-breakdown.component';

export class PortfolioComponentConfig {
  // private to disallow creating other instances of this type
  private constructor(
    public readonly componentId: PortfolioComponentId,
    public readonly component: Type<any>,
    public readonly baseType: PortfolioBaseType,
    public readonly gqlQuery: DocumentNode,
    // TODO: replace Mapper<any> with PortfolioMapper
    public readonly mapper: Type<Mapper<any>>,
    // used for labels, caveats etc e.g. "top-ten-holding-money-funds"
    public readonly baseLabelKey?: string
  ) {}
  // config class based on this https://stackoverflow.com/a/51398471

  // chart components
  static readonly ASSET_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.ASSET_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    assetAllocationQuery,
    AssetAllocationMapper
  );

  static readonly TOP_TEN_HOLDINGS = new PortfolioComponentConfig(
    PortfolioComponentId.TOP_TEN_HOLDINGS,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    topHoldingsQuery,
    TopHoldingsMapper
  );

  static readonly TOP_TEN_HOLDINGS_TABLE = new PortfolioComponentConfig(
    PortfolioComponentId.TOP_TEN_HOLDINGS,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    topHoldingsQuery,
    TopHoldingsTableMapper
  );

  static readonly SECTOR_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.SECTOR_ALLOCATION,
    SectorBreakdownComponent,
    PortfolioBaseType.SECTOR_BREAKDOWN,
    sectorAllocationQuery,
    SectorAllocationsTableMapper,
    'sector-allocation'
  );

  static allConfigs: PortfolioComponentConfig[] = [
    PortfolioComponentConfig.ASSET_ALLOCATION,
    PortfolioComponentConfig.TOP_TEN_HOLDINGS,
    PortfolioComponentConfig.SECTOR_ALLOCATION,
  ];

  // returns matching config or undefined
  static getConfig(
    componentId: PortfolioComponentId
  ): PortfolioComponentConfig {
    return this.allConfigs.find(
      (config: PortfolioComponentConfig): boolean =>
        config.componentId === componentId
    ); // just match on key
  }

  toString() {
    return this.componentId;
  }
}
